import React from 'react';
import { arrayOf, bool, func, node, number, object, shape, string } from 'prop-types';
import classNames from 'classnames';

import Field, { hasDataInFields } from '../../Field';
import BlockBuilder from '../../BlockBuilder';

import SectionContainer from '../SectionContainer';
import { AspectRatioWrapper } from '../../../../components';
import { withViewport } from '../../../../util/uiHelpers';
import css from './SectionHowItWorks.module.css';

import skatingIllustration from './images/skatingIllustration.svg';
import skatingIllustrationMobile from './images/skatingIllustrationMobile.svg';
import checklistIllustration from './images/checklistIllustration.svg';

const MAX_MOBILE_SCREEN_WIDTH = 768;

// The number of columns (numColumns) affects styling and responsive images
const COLUMN_CONFIG = [
  { css: css.oneColumn, responsiveImageSizes: '(max-width: 767px) 100vw, 1200px' },
  { css: css.twoColumns, responsiveImageSizes: '(max-width: 767px) 100vw, 600px' },
  { css: css.threeColumns, responsiveImageSizes: '(max-width: 767px) 100vw, 400px' },
  { css: css.fourColumns, responsiveImageSizes: '(max-width: 767px) 100vw, 265px' },
];
const getIndex = numColumns => numColumns - 1;
const getColumnCSS = numColumns => {
  const config = COLUMN_CONFIG[getIndex(numColumns)];
  return config ? config.css : COLUMN_CONFIG[0].css;
};
const getResponsiveImageSizes = numColumns => {
  const config = COLUMN_CONFIG[getIndex(numColumns)];
  return config ? config.responsiveImageSizes : COLUMN_CONFIG[0].responsiveImageSizes;
};

// Section component that's able to show blocks in multiple different columns (defined by "numColumns" prop)
const SectionHowItWorks = props => {
  const {
    sectionId,
    className,
    rootClassName,
    defaultClasses,
    numColumns,
    title,
    description,
    appearance,
    callToAction,
    blocks,
    isInsideContainer,
    options,
    viewport,
  } = props;

  // If external mapping has been included for fields
  // E.g. { h1: { component: MyAwesomeHeader } }
  const fieldComponents = options?.fieldComponents;
  const fieldOptions = { fieldComponents };

  const hasHeaderFields = hasDataInFields([title, description, callToAction], fieldOptions);
  const hasBlocks = blocks?.length > 0;

  const secondaryCtaData = {
    fieldType: 'internalButtonLink',
    href: '/s',
    content: 'Jetzt starten',
  };

  const isMobileLayout = viewport.width <= MAX_MOBILE_SCREEN_WIDTH;

  const aspectWidth = isMobileLayout ? 2 : 16;
  const aspectHeight = isMobileLayout ? 1 : 9;

  return (
    <div className={css.mainContainer}>
      <SectionContainer
        id={sectionId}
        className={classNames(className, css.howItWorks)}
        rootClassName={rootClassName}
        appearance={appearance}
        options={fieldOptions}
      >
        <AspectRatioWrapper
          className={css.aspectRatioWrapper}
          width={aspectWidth}
          height={aspectHeight}
        >
          <img
            className={css.skatingIllustration}
            src={isMobileLayout ? skatingIllustrationMobile : skatingIllustration}
            alt="skating"
          />
        </AspectRatioWrapper>

        {hasBlocks ? (
          <div
            className={classNames(
              defaultClasses.blockContainer,
              css.blockContainer,
              getColumnCSS(numColumns),
              {
                [css.noSidePaddings]: isInsideContainer,
              }
            )}
          >
            <BlockBuilder
              className={css.block}
              ctaButtonClass={defaultClasses.ctaButton}
              mediaClassName={css.media}
              textClassName={css.text}
              blocks={blocks}
              sectionId={sectionId}
              responsiveImageSizes={getResponsiveImageSizes(numColumns)}
              options={options}
            />
          </div>
        ) : null}
        {hasHeaderFields ? (
          <header className={classNames(css.sectionDetails, defaultClasses.sectionDetails)}>
            <img
              className={css.checklistIllustration}
              src={checklistIllustration}
              alt="checklist"
            />
            <Field
              data={title}
              className={classNames(css.title, defaultClasses.title)}
              options={fieldOptions}
            />
            <Field
              data={description}
              className={classNames(css.description, defaultClasses.description)}
              options={fieldOptions}
            />
            <div className={css.ctaContainer}>
              <Field
                data={callToAction}
                className={classNames(defaultClasses.ctaButton, css.ctaButton)}
                options={fieldOptions}
              />
              <Field
                data={secondaryCtaData}
                className={classNames(defaultClasses.ctaButton, css.ctaButton, css.secondaryCta)}
                options={fieldOptions}
              />
            </div>
          </header>
        ) : null}
      </SectionContainer>
    </div>
  );
};

const propTypeOption = shape({
  fieldComponents: shape({ component: node, pickValidProps: func }),
});

SectionHowItWorks.defaultProps = {
  className: null,
  rootClassName: null,
  defaultClasses: null,
  textClassName: null,
  numColumns: 1,
  title: null,
  description: null,
  appearance: null,
  callToAction: null,
  blocks: [],
  isInsideContainer: false,
  options: null,
};

SectionHowItWorks.propTypes = {
  sectionId: string.isRequired,
  className: string,
  rootClassName: string,
  defaultClasses: shape({
    sectionDetails: string,
    title: string,
    description: string,
    ctaButton: string,
  }),
  numColumns: number,
  title: object,
  description: object,
  appearance: object,
  callToAction: object,
  blocks: arrayOf(object),
  isInsideContainer: bool,
  options: propTypeOption,
};

export default withViewport(SectionHowItWorks);
